import React, { useEffect, useMemo, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { Row, Col, Form, Modal } from "react-bootstrap";

import BGImage from "../assets/confirmed-bg-image.png";
import classes from "./BookConfirmed.module.scss";
import { ReactComponent as GooglePlay } from "../assets/google-play-icon.svg";
import { ReactComponent as AppStore } from "../assets/ios-app-store-icon.svg";
import { ReactComponent as EmailIcon } from "../assets/icon-email.svg";
import { ReactComponent as UnitIcon } from "../assets/icon-unit.svg";
import { ReactComponent as IdentityIcon } from "../assets/icon-identity-icon.svg";
import { ReactComponent as PhoneIcon } from "../assets/icon-phone.svg";
import { ReactComponent as RecurringIcon } from "../assets/icon-recurring.svg";

import * as Constants from "../features/generalConstants";
import { LineItem } from "../features/rapidstor/entities/LineItem";
import Location from "../features/rapidstor/entities/Location";
import { productTransformer } from "../features/rapidstor/entities/Merchandise";
import { addVat, formatCurrency, monthlyToWeekly } from "../features/helpers";

import PrimaryButton from "../components/Elements/PrimaryButton";
import PaymentModal from "../components/Elements/PaymentModal";
import {
  useGetChargesQuery,
  useGetMerchandiseQuery,
  useGetReservationQuery,
  usePostChargesMerchMutation,
  usePostScaPaymentMutation,
} from "../features/rapidstor/rapidstorApiSlice";
import { SubmitState } from "./BookConfirm";
import {
  RapidStorChargesMerchRequest,
  RapidStorPaymentResult,
} from "../services/rapidstor";
import { useSelector } from "react-redux";
import { selectReservationData, selectReservationToken } from "../store/bookingSlice";
import { locationCompanyProfile } from "../features/helpers";
import { Logger } from "../main";

interface Props {
  withError?: boolean;
}

export interface BookingForm {
  termsAndConditions: boolean;
  allowMarketing: boolean;
  cardType: string;
  cardName: string;
  cardNumber: number;
  cardExpMonth: number;
  cardExpYear: number;
  cardCVV: number;
}

function BookConfirmed({ withError }: Props) {
  /**
   * GENERAL PAGE STATE
   */
  const location = useLoaderData() as Location;
  const reservationData = useSelector(selectReservationData);
  const reservationToken = useSelector(selectReservationToken);
  const { reservationTokenParams } = useParams();
  const { data: reservation, refetch: refetchReservation } = useGetReservationQuery({
    locationCode: location.locationCode,
    token: reservationToken || reservationTokenParams,
  });
  const navigate = useNavigate();
  const appStores = locationCompanyProfile();

  /**
   * BOX SHOP RELATED STATE
   */
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  /**
   * UI CONTROLLING STATE
   */
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [moveInError, setMoveInError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  // const [skipExtrasQueries, setSkipExtrasQueries] = useState<boolean>(false);
  const [formClass, setFormClass] = useState<string>(classes.form);
  const [submissionState, setSubmissionState] = useState<SubmitState>("init");

  /**
   * QUERIES
   */
  const [addChargesAndMerch] = usePostChargesMerchMutation();
  const [makeScaPayment] = usePostScaPaymentMutation();
  const { data: merchandise } = useGetMerchandiseQuery(location.locationCode);
  const { data: charges } = useGetChargesQuery(location.locationCode);

  const [form, updateForm] = useState<BookingForm>({
    termsAndConditions: false,
    allowMarketing: true,
    // PAYMENT DETAILS
    cardType: "5",
    cardName: "",
    cardNumber: 0,
    cardExpMonth: 0,
    cardExpYear: 0,
    cardCVV: 0,
  });

  useEffect(() => {
    if (reservationTokenParams && !reservationData) {
      refetchReservation();
    }

    refetchReservation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reservation?.ledgerId) {
      if (window.rapidstor_api_tracking_listener) {
        try {
          window.rapidstor_api_tracking_listener("terms-and-conditions-accepted", {
            locationCode: location.locationCode,
            ledgerId: reservation.ledgerId,
          });
        } catch (err) {
          Logger().log(JSON.stringify(err));
        }
      }
    }
  }, [location.locationCode, reservation]);

  useEffect(() => {
    const setupLineItems = () => {
      const initialItems: LineItem[] = [];
      if (charges) {
        Constants.CHARGES.forEach((c) => {
          for (const charge of charges) {
            if (c.desc === charge.sChgDesc) {
              initialItems.push({
                name: c.name,
                line_item_name: c.line_item_name,
                _id: charge._id,
                key: c.key,
                total: addVat(charge.dcPrice, charge.dcTax1Rate / 100),
                active: false,
                type: c.type,
                description: c.description,
                category: "packaging-+-extras",
              });
            }
          }
        });
      }
      if (Constants.BULLET_PADLOCK_LOCATIONS.includes(location.locationCode)) {
        initialItems.push(Constants.PADLOCK_TYPES[1]);
      } else if (Constants.PADLOCK_LOCATIONS.includes(location.locationCode)) {
        initialItems.push({
          ...Constants.PADLOCK_TYPES[0],
          _id: merchandise?.products.find((m) => m.name === "Standard Door Lock")?._id,
        });
      } else {
        initialItems.push(Constants.PADLOCK_TYPES[2]);
      }
      const merchTempArr: LineItem[] = [];
      if (merchandise && merchandise.products.length) {
        for (const merch of merchandise.products) {
          if (Constants.BUNDLE_PRODUCTS.includes(merch.name)) {
            merchTempArr.push(productTransformer(merch));
          }
        }
      }
      merchTempArr.sort((a, b) => a.total - b.total);
      for (const m of merchTempArr) {
        initialItems.push(m);
      }
      setLineItems(initialItems);
    };

    setupLineItems();
  }, [charges, location.locationCode, merchandise]);

  const subTotal = useMemo(() => {
    let total = 0.0;
    for (const item of lineItems) {
      if (item.active) {
        if (item.discountedRate) {
          total += item.discountedRate;
        } else {
          total += item.total;
        }
      }
    }
    return formatCurrency(total);
  }, [lineItems]);

  const setOption = (option: string, value?: boolean) => {
    const items = lineItems;
    for (const i of items) {
      if (i.key === option) {
        i.active = value ?? !i.active;
        break;
      }
    }
    setLineItems([...items]);
  };

  const handlePaymentResult = async (result: RapidStorPaymentResult) => {
    Logger().log(JSON.stringify(result));
    if (result.error === null) {
      return;
    }
    if (result.error) {
      if (submissionState !== "prepareMoveIn") {
        setMoveInError("An error has occured after processing your move in (payment)");
        setShowPaymentModal(false);
        setSubmissionState("partialComplete");
        return;
      }
      setMoveInError(
        "An error has occured while attempting to process your move in (payment)"
      );
      setShowPaymentModal(false);
      setSubmissionState("error");
      return;
    }
    if (result && !result.error) {
      setShowPaymentModal(false);
      switch (submissionState) {
        case "prepareMoveIn":
          setSubmissionState("moveIn");
          break;
        case "prepareBillingInfoUpdate":
          setSubmissionState("billingInfoUpdate");
          break;
        case "preparePayment":
          setSubmissionState("payment");
          break;
        default:
          Logger().error(
            `Unexpected submission state: ${submissionState} ${JSON.stringify(result)}`
          );
          setSubmissionState("error");
          setMoveInError("An unexpected error has occured. Please try again.");
      }
      return;
    }
  };

  useEffect(() => {
    scroll({ top: 200, behavior: "smooth" });
  }, []);

  useEffect(() => {
    Logger().log(`Submission state: ${submissionState}`);
    const resToken = reservationToken ? reservationToken : reservationTokenParams;
    if (!resToken) {
      return;
    }

    const handleSubmitState = async () => {
      switch (submissionState) {
        case "init":
          return;
        case "preparePayment":
          // If there are no extras or boxes, skip payment
          if (
            !lineItems.some(
              (li) => li.active && (li.type === "extras" || li.type === "Boxes")
            )
          ) {
            setSubmissionState("complete");
            return;
          }
          try {
            if (!reservation?.tenantId || !reservation?.UnitID) {
              throw new Error("Missing tenantId or unitId");
            }
            const resp = await makeScaPayment(
              scaPaymentPayload(reservation.tenantId, reservation.UnitID)
            ).unwrap();
            openPaymentWindow(resp.redirect);
          } catch (err) {
            Logger().error(
              `Error preparing your paying for your boxes: ${JSON.stringify(err)}`
            );
            navigate(`/${location.slug}/confirmed-with-error/${reservationToken}`);
          }
          break;
        case "payment":
          setSubmissionState("complete");
          break;
        case "complete":
          setSubmitting(false);
          scroll({ top: 200, behavior: "smooth" });
          // navigate(`/${location.slug}/confirmed/${reservationToken}`);
          break;
        case "partialComplete":
          setSubmitting(false);
          scroll({ top: 200, behavior: "smooth" });
          // navigate(`/${location.slug}/confirmed-with-error/${reservationToken}`);
          break;
        case "error":
          setSubmitting(false);
          scroll({ top: 200, behavior: "smooth" });
          break;
      }
    };
    handleSubmitState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionState]);

  const openPaymentWindow = (url: string) => {
    setPaymentLink(url);
    setShowPaymentModal(true);
  };

  const scaPaymentPayload = (tenantId: number, unitId: number) => {
    return {
      locationCode: location.locationCode,
      payload: {
        chargeTotal: subTotal,
        cardHoldersName: form.cardName,
        cardNumber: form.cardNumber,
        cardExpiryMonth: form.cardExpMonth,
        cardExpiryYear: form.cardExpYear,
        cardCVV: form.cardCVV,
        cardTypeId: parseInt(form.cardType),
        tenantId,
        paymentAllocation: {
          [unitId]: subTotal,
        },
        view: "standalone",
        parentUri: window.location.href,
      },
    };
  };

  const completeBooking = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formElement = e.target as HTMLFormElement;
    setFormClass(`${classes.form} ${classes.was_validated}`);
    if (!formElement.checkValidity()) {
      // Focus on first invalid input
      const invalid = formElement.querySelector(":invalid");
      if (invalid) {
        setSubmitting(false);
        const input = invalid as HTMLInputElement;
        input.focus();
      }
      return;
    }

    if (!reservation?.tenantId || !reservation?.ledgerId || !reservation?.UnitID) {
      Logger().error(`Missing tenantId or ledgerId: ${JSON.stringify(reservation)}`);
      return;
    }

    setSubmitting(true);

    const merchRequest: RapidStorChargesMerchRequest = {
      locationCode: location.locationCode,
      tenantId: reservation.tenantId,
      ledgerId: reservation.ledgerId,
      payload: {
        cart: {},
        charges: {},
      },
    };
    lineItems.map((item: LineItem) => {
      if (item.active === true) {
        const itemId = item._id;
        if (!itemId) {
          return;
        }
        switch (item.type) {
          case "Boxes":
            merchRequest.payload.cart[itemId] = 1;
            break;
          case "extra":
            if (item.key === "padlock_required") {
              merchRequest.payload.cart[itemId] = 1;
            } else {
              merchRequest.payload.charges[itemId] = 1;
            }
            break;
          default:
            break;
        }
      }
    });
    try {
      await addChargesAndMerch(merchRequest).unwrap();
      setSubmissionState("preparePayment");
    } catch (err) {
      Logger().error(`Error patching reservation: ${JSON.stringify(err)}`);
      setSubmissionState("error");
      setSubmitting(false);
      setMoveInError("There was an error checking out. Please try again.");
    }
  };

  const update = (event: React.BaseSyntheticEvent) => {
    const { name, value } = event.target;
    updateForm((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  return (
    <>
      <Row>
        <Col style={{ padding: "12px" }}>
          <div className={classes.welcome_aboard}>
            <img src={BGImage} />
            <div className={classes.overlay}></div>
            {withError ? <h4>Welcome aboard!</h4> : <h4>Welcome aboard!</h4>}
            {withError ? (
              <h5>Please contact our team to confirm your booking</h5>
            ) : (
              <h5>Your space is ready & waiting</h5>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <h4 className={classes.header_text}>Next Steps</h4>
          {withError ? (
            <div className={classes.steps_card}>
              <div className={classes.top}>
                <span>1. {Constants.BOOK_CONFIRMED.STEP_0}</span>
                <div>
                  <PhoneIcon />
                </div>
              </div>
            </div>
          ) : null}
          <div className={classes.steps_card}>
            <div className={classes.top}>
              <span>
                {withError ? 2 : 1}. {Constants.BOOK_CONFIRMED.STEP_1}
              </span>
              <div>
                <EmailIcon />
              </div>
            </div>
          </div>
          <div className={classes.steps_card} style={{ display: "block" }}>
            <div className={classes.top}>
              <span>
                {withError ? 3 : 2}. {Constants.BOOK_CONFIRMED.STEP_2}
              </span>
            </div>
            <div className={classes.app_store}>
              <a
                // href="https://play.google.com/store/apps/details?id=com.storapp.app.csbt.ss"
                href={appStores["google-play"]}
                target="_blank"
                rel="noreferrer"
              >
                <GooglePlay />
              </a>
              <a
                // href="https://apps.apple.com/us/app/surestore/id1673189879"
                href={appStores["app-store"]}
                target="_blank"
                rel="noreferrer"
              >
                <AppStore />
              </a>
            </div>
          </div>
          <div className={classes.steps_card}>
            <div className={classes.top}>
              <span>
                {withError ? 4 : 3}. {Constants.BOOK_CONFIRMED.STEP_3}
              </span>
              <div>
                <IdentityIcon />
              </div>
            </div>
          </div>
          <div className={classes.steps_card}>
            <div className={classes.top}>
              <span>
                {withError ? 5 : 4}. {Constants.BOOK_CONFIRMED.STEP_4}
              </span>
              <div>
                <RecurringIcon width={27} />
              </div>
            </div>
          </div>
          <div className={classes.steps_card}>
            <div className={classes.top}>
              <span>
                {withError ? 6 : 5}. {Constants.BOOK_CONFIRMED.STEP_5}
              </span>
              <div>
                <UnitIcon />
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <Row hidden={submissionState != "complete"}>
        <Col xs={12}>
          <div className={classes.banner}>
            <h4>Thank You For Choosing SureStore</h4>
          </div>
        </Col>
      </Row>
      <Form
        className={formClass}
        hidden={submissionState === "complete"}
        noValidate={true}
        onSubmit={(e) => completeBooking(e)}
      >
        {/* PACKAGING BUNDLE OFFER */}
        <Row>
          <h4 className={classes.section_header}>
            {Constants.BOOKING_FORM.SECTION_HEADERS.PACKAGING_BUNDLE}
          </h4>
          <div className={classes.box_shop}>
            {lineItems.map((item) => {
              if (item.type === "Boxes" && item.contents && item.discountedRate) {
                return (
                  <div
                    key={item._id}
                    className={classes.box_shop_col}
                    onClick={(e) => {
                      if (e.target) {
                        // Avoid toggling when clicking on input.
                        const target = e.target as HTMLElement;
                        if (target.tagName === "INPUT") {
                          return;
                        }
                      }
                      setOption(item.key);
                    }}
                  >
                    <div className={classes.card}>
                      <div
                        className={`${item.active ? classes.active : ""} ${
                          classes.primary
                        }`}
                      >
                        <div className={classes.left}>
                          <Form.Check
                            name={item.key}
                            checked={item.active ? true : false}
                            onChange={(e) => setOption(e.currentTarget.name)}
                          ></Form.Check>
                        </div>
                        <div className={classes.right}>
                          <h6>{item.name}</h6>
                          <Row>
                            {item.contents.map((c, i) => {
                              // const inv = c as ProductPackInventory;
                              return (
                                <Col
                                  key={`${item._id}_idx_${i}`}
                                  xs={6}
                                  style={{ padding: "0px" }}
                                >
                                  <li style={{ fontSize: "12px" }}>{c.toString()}</li>
                                </Col>
                              );
                            })}
                          </Row>
                        </div>
                        <div className={`${classes.pricing}`}>
                          <h6>£{item.discountedRate}</h6>
                          <div
                            className={`${classes.savings_box} ${
                              item.active ? classes.active : ""
                            }`}
                          >
                            <span>SAVE</span>
                            <span>£{Math.round(item.total - item.discountedRate)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        </Row>
        {/* EXTRA & ADD ONS */}
        <Row>
          <Col>
            <h4
              hidden={!lineItems.some((item) => item.type === "extra")}
              className={classes.section_header}
            >
              {Constants.BOOKING_FORM.SECTION_HEADERS.EXTRAS}
            </h4>
            <>
              {lineItems.map((item) => {
                if (item.type === "extra") {
                  return (
                    <div
                      key={item.key}
                      className={classes.card}
                      onClick={() => {
                        if (!item.total) {
                          return;
                        }
                        setOption(item.key);
                      }}
                    >
                      <div
                        className={`${item.active ? classes.active : ""} ${
                          classes.primary
                        }`}
                      >
                        <div className={classes.left}>
                          <Form.Check
                            name={item.key}
                            checked={item.active}
                            readOnly={true}
                          ></Form.Check>
                        </div>
                        <div className={`${classes.right}`}>
                          <h6 className={`${item.total ? "" : classes.line_through}`}>
                            {item.name}
                          </h6>
                          <span>{item.description}</span>
                        </div>
                        <div className={`${classes.pricing}`}>
                          <h6>
                            {item.total
                              ? `£${
                                  item.key === "padlock_required"
                                    ? item.total
                                    : monthlyToWeekly(item.total)
                                }`
                              : "Free!"}
                          </h6>
                          <span>
                            {item.key === "padlock_required"
                              ? item.total
                                ? "one off cost"
                                : ""
                              : "per week"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </>
          </Col>
        </Row>
        {/* BOOKING SUMMARY SECTION */}
        <Row hidden={parseFloat(subTotal) === 0}>
          <Col xs={12} sm={12} md={6}>
            <h4 className={classes.section_header}>Shopping Basket</h4>
            {["packaging-+-extras"].map((category) => {
              if (!lineItems.find((item) => item.active && item.category === category)) {
                return null;
              }
              return (
                <div
                  className={classes.booking_summary}
                  key={`booking-summary-${category}`}
                >
                  {lineItems.map((item, idx) => {
                    if (item.active === true && item.category === category) {
                      return (
                        <div key={`line_item_idx_${idx}`} className={classes.line_item}>
                          <div className={classes.item}>
                            <span>{item.line_item_name ?? item.name}</span>
                            <span
                              className={
                                item.discounted && item.key === "standard_rate"
                                  ? classes.discounted
                                  : ""
                              }
                            >
                              £
                              {item.discounted && item.key != "standard_rate"
                                ? formatCurrency(item.discountedRate)
                                : formatCurrency(item.total)}
                            </span>
                          </div>
                        </div>
                      );
                    }
                  })}
                  {/* {new Set(
                        lineItems
                          .filter((item) => item.active)
                          .map((item) => item.category)
                      ).size > 1 ? (
                        <div className={classes.total_item}>
                          <span>{category.replace(/-/g, " ")}</span>
                          <span>
                            £
                            {formatCurrency(
                              lineItems.reduce((acc: number, item) => {
                                if (item.active && item.category === category) {
                                  return (
                                    acc +
                                    (item.discounted && item.discountedRate !== undefined
                                      ? item.discountedRate
                                      : item.total)
                                  );
                                }
                                return acc;
                              }, 0)
                            )}
                          </span>
                        </div>
                      ) : null} */}
                </div>
              );
            })}
            <div className={classes.booking_summary}>
              <div className={classes.total_item}>
                <span>
                  <strong>Total to pay today</strong>
                </span>
                <span>
                  <strong>£{subTotal}</strong>
                </span>
              </div>
            </div>
          </Col>

          {/* PAYMENT DETAILS */}
          <Col xs={12} sm={12} md={6}>
            <h4 className={classes.section_header}>
              {Constants.BOOKING_FORM.SECTION_HEADERS.PAYMENT_DETAILS}
            </h4>
            <Row>
              <Col xs={12} sm={12} md={6}>
                <Form.Label>Card Type</Form.Label>
                <Form.Select
                  required
                  name="cardType"
                  value={form.cardType}
                  onChange={(e) => update(e)}
                >
                  {Constants.CARD_TYPES.map((type) => {
                    return (
                      <option key={type.type} value={type.type}>
                        {type.name}
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>
              <Col xs={12} sm={12} md={12}>
                <Form.Label>Cardholder&apos;s Name</Form.Label>
                <Form.Control
                  type={"text"}
                  required
                  name="cardName"
                  autoComplete="cc-name"
                  placeholder="Cardholder's Name"
                  value={form.cardName}
                  onChange={(e) => update(e)}
                />
              </Col>
              <Col xs={12} sm={12} md={12}>
                <Form.Label>Card Number</Form.Label>
                <Form.Control
                  type={"number"}
                  className={classes.numberinput}
                  required
                  name="cardNumber"
                  placeholder="Card Number"
                  autoComplete="cc-number"
                  value={undefined}
                  onChange={(e) => update(e)}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6} sm={6} md={5}>
                <Form.Label>Exp</Form.Label>
                <div className={classes.formExpiry}>
                  <Form.Control
                    type={"text"}
                    required
                    name="cardExpMonth"
                    placeholder="MM"
                    autoComplete="cc-exp-month"
                    value={undefined}
                    onChange={(e) => {
                      update(e);
                    }}
                  />
                  <Form.Control
                    type={"text"}
                    required
                    name="cardExpYear"
                    placeholder="YY"
                    autoComplete="cc-exp-year"
                    value={undefined}
                    onChange={(e) => {
                      update(e);
                    }}
                  />
                </div>
              </Col>
              <Col xs={6} sm={6} md={4}>
                <Form.Label>CVV</Form.Label>
                <Form.Control
                  type={"number"}
                  className={classes.numberinput}
                  required
                  name="cardCVV"
                  autoComplete="cc-csc"
                  value={undefined}
                  onChange={(e) => update(e)}
                />
              </Col>
            </Row>
            {/* <div className={classes.check_section}>
                  <Form.Check
                    name={"autoBill"}
                    id="autoBill"
                    value={form.autoBill ? "on" : "off"}
                    checked={form.autoBill}
                    onChange={(e) => setCheckbox(e)}
                  ></Form.Check>
                  <label htmlFor="autoBill">Auto bill my card to avoid late fees</label>
                </div> */}
            {/* <span className={classes.tiny_text}>
                  {Constants.BOOKING_FORM.PAYMENT_PROMPT}
                </span> */}
          </Col>
          {/* PAYMENT MODAL */}
          <Modal show={showPaymentModal} centered>
            <Modal.Body className="">
              <PaymentModal
                paymentLink={paymentLink}
                onPaymentResult={handlePaymentResult}
              />
            </Modal.Body>
          </Modal>
        </Row>
        <hr hidden={parseFloat(subTotal) === 0} />
        {/* TERMS */}
        <Row style={{ marginBottom: "40px" }} hidden={parseFloat(subTotal) === 0}>
          <Col xs={12} sm={12} md={7}>
            {" "}
          </Col>
          <Col xs={12} sm={12} md={5}>
            <div className={classes.final_section}>
              <div className={classes.subtotal}>
                <span className={classes.value}>£{subTotal}</span>
                <span> Due today</span>
              </div>
              {moveInError && (
                <div className={classes.moveInError}>
                  <h4>ERROR: {moveInError}</h4>
                </div>
              )}
              <PrimaryButton submit responsive disabled={submitting} loading={submitting}>
                {submitting
                  ? Constants.BOOKING_FORM.BUTTON_TEXT_SUBMITTING
                  : Constants.BOOKING_FORM.BUTTON_TEXT}
              </PrimaryButton>
            </div>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default BookConfirmed;
